import React, { useEffect, useState } from 'react';
import type { IshikawaHypothesis, Issue, RootCauseCategory } from '../../types/issue';
import { ISSUE_SEVERITY_LABELS, ISSUE_STATUS_LABELS, ROOT_CAUSE_CATEGORY_LABELS } from '../../types/issue';
import type { EnterpriseAction } from '../../services/actionService';
import { ACTION_STATUS_LABELS } from '../../services/actionService';
import type { ActionCountermeasureRecord } from '../../services/actionCountermeasureService';
import type { GembaNoteRecord, IssueMaspEventRecord, IssueMeasureRecord, StandardizationEventRecord, StratumRecord } from '../../services/issueMaspService';
import type { IssueEvidenceRecord } from '../../services/issueEvidenceService';
import { evidenceSignedUrl } from '../../services/issueEvidenceService';
import {
  authorizingAttempts,
  efficacyAttempts,
  evaluateIssueClosure,
  latestEfficacyAttempt,
  latestReopenAt,
} from '../../utils/efficacyCheck';
import { isCauseConfirmed } from '../../utils/problemCause';
import { ensureIshikawa, hypothesisText } from '../../utils/causeDraft';
import { formatMeasureValue, presentMeasureComparison } from '../../utils/issueMasp';
import { paretoSeries } from '../../utils/pareto';
import { workspaceStages } from '../../utils/issueProgress';
import { presentStageLabel } from '../../utils/structuredStageLabel';
import { executionProgress } from '../../utils/countermeasurePlan';
import {
  AUDIT_RESULT_LABEL,
  STANDARDIZATION_KIND_LABEL,
  formatEventDate,
  type StandardizationKind,
} from '../../utils/standardization';
import { conclusionDurationDays } from '../../utils/conclusion';

interface A3ReportProps {
  issue: Issue;
  actions: EnterpriseAction[];
  measures: IssueMeasureRecord[];
  strata: StratumRecord[];
  gemba: GembaNoteRecord[];
  countermeasures: ActionCountermeasureRecord[];
  events: StandardizationEventRecord[];
  executionEvents?: IssueMaspEventRecord[];
  evidence?: IssueEvidenceRecord[];
  assigneeNames: Record<string, string>;
  onClose: () => void;
}

const PRINT_CSS = `
@media print {
  @page { size: A3 landscape; margin: 8mm; }
  body * { visibility: hidden !important; }
  .a3-sheet, .a3-sheet * { visibility: visible !important; }
  .a3-sheet {
    position: absolute !important;
    inset: 0 auto auto 0 !important;
    width: 404mm !important;
    max-width: none !important;
    min-width: 0 !important;
    margin: 0 !important;
    box-shadow: none !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .a3-no-print { display: none !important; }
}
`;

export const A3Report: React.FC<A3ReportProps> = (props) => {
  const direct = props.actions.filter((action) => action.problem_id === props.issue.id);
  return (
    <div className="overflow-x-auto">
      <style>{PRINT_CSS}</style>
      <div className="a3-no-print mb-2 flex items-center justify-end gap-3">
        <button type="button" onClick={() => window.print()} className="text-[12px] font-semibold text-[var(--brand-primary)] hover:underline">
          Imprimir A3
        </button>
        <button type="button" onClick={props.onClose} className="text-[12px] font-medium text-slate-600 hover:underline">
          Fechar
        </button>
      </div>
      <article
        aria-label="A3 do problema"
        className="a3-sheet min-w-[1080px] border-2 border-[#1e3a5f] bg-white text-slate-900"
      >
        <A3Header {...props} />
        <div className="grid grid-cols-2">
          <div className="border-r border-[#1e3a5f]">
            <Identification issue={props.issue} measures={props.measures} />
            <Observation strata={props.strata} measures={props.measures} gemba={props.gemba} evidence={props.evidence ?? []} />
            <CauseAnalysis issue={props.issue} />
          </div>
          <div>
            <ActionPlan actions={direct} countermeasures={props.countermeasures} assigneeNames={props.assigneeNames} />
            <Execution actions={direct} events={props.executionEvents ?? []} />
            <Results issue={props.issue} actions={direct} measures={props.measures} />
            <Standardization events={props.events} />
            <Conclusion issue={props.issue} />
          </div>
        </div>
      </article>
    </div>
  );
};

function A3Header({ issue, assigneeNames }: A3ReportProps) {
  const stage = workspaceStages(issue).find((item) => item.statuses.includes(issue.status));
  const status = stage ? presentStageLabel(issue.mode, stage.key, stage.label) : ISSUE_STATUS_LABELS[issue.status];
  const place = [issue.department, issue.process, issue.step].map((item) => item?.trim() || '').filter(Boolean).join(' · ');
  const owner = issue.assigned_to ? (assigneeNames[issue.assigned_to] ?? '—') : '—';
  return (
    <header className="grid grid-cols-[9rem_minmax(0,1fr)_18rem] border-b-2 border-[#1e3a5f]">
      <div className="flex flex-col justify-center bg-[var(--brand-primary)] px-3 py-2 text-white">
        <p className="text-[10px] font-semibold tracking-[0.18em]">DATAFLOW</p>
        <p className="text-[18px] font-bold leading-none">A3 / MASP</p>
      </div>
      <div className="flex items-center px-3 py-2">
        <h2 className="text-[16px] font-bold leading-tight">{issue.title}</h2>
      </div>
      <dl className="grid grid-cols-[5.2rem_minmax(0,1fr)] border-l border-[#1e3a5f] text-[10px]">
        <Meta label="Área" value={place || '—'} />
        <Meta label="Responsável" value={owner} />
        <Meta label="Abertura" value={issue.created_at ? new Date(issue.created_at).toLocaleDateString('pt-BR') : '—'} />
        <Meta label="Status" value={status} />
        <Meta label="Severidade" value={ISSUE_SEVERITY_LABELS[issue.severity]} />
      </dl>
    </header>
  );
}

function Meta({ label, value }: { label: string; value: string }) {
  return (
    <>
      <dt className="border-b border-slate-200 bg-slate-50 px-1.5 py-0.5 font-semibold uppercase tracking-wide text-slate-500">{label}</dt>
      <dd className="truncate border-b border-slate-200 px-1.5 py-0.5">{value}</dd>
    </>
  );
}

function Block({ number, title, children }: { number: string; title: string; children: React.ReactNode }) {
  return (
    <section className="border-b border-[#1e3a5f]">
      <h3 className="bg-[#1e3a5f] px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide text-white">
        {number}. {title}
      </h3>
      <div className="px-2 py-1.5">{children}</div>
    </section>
  );
}

function Identification({ issue, measures }: { issue: Issue; measures: IssueMeasureRecord[] }) {
  const phenomenon = issue.root_cause_analysis?.problem_statement?.trim() || issue.description?.trim() || '—';
  const losses = measures.filter((measure) => measure.role === 'loss');
  const controls = measures.filter((measure) => measure.role === 'control');
  return (
    <Block number="1" title="Identificação do problema">
      <p className="text-[12px] leading-snug">{phenomenon}</p>
      <div className="mt-1 grid grid-cols-2 gap-2">
        <div>
          <p className="text-[10px] font-semibold uppercase text-slate-500">Impacto / perdas</p>
          {losses.length === 0 ? <Empty /> : losses.map((measure) => {
            const item = presentMeasureComparison(measure);
            return <p key={measure.id} className="text-[11px]">{measure.name}: {formatMeasureValue(item.before)} {measure.unit}</p>;
          })}
        </div>
        <div>
          <p className="text-[10px] font-semibold uppercase text-slate-500">Medida de controle</p>
          {controls.length === 0 ? <Empty /> : controls.map((measure) => {
            const item = presentMeasureComparison(measure);
            return <p key={measure.id} className="text-[11px]">{measure.name}: {formatMeasureValue(item.before)} → {formatMeasureValue(item.target)} {measure.unit}</p>;
          })}
        </div>
      </div>
    </Block>
  );
}

function Observation({
  strata,
  measures,
  gemba,
  evidence,
}: {
  strata: StratumRecord[];
  measures: IssueMeasureRecord[];
  gemba: GembaNoteRecord[];
  evidence: IssueEvidenceRecord[];
}) {
  const charts = strata
    .map((stratum) => ({ stratum, series: paretoSeries(stratum.slices, (id) => measures.find((measure) => measure.id === id)?.unit ?? null) }))
    .filter((item) => item.series);
  return (
    <Block number="2" title="Observação">
      {charts.length === 0 && strata.every((item) => item.slices.length === 0) ? <Empty /> : null}
      {charts.map(({ stratum, series }) => series ? (
        <div key={stratum.id} className="mb-1" aria-label={`Pareto ${stratum.dimension}`}>
          <p className="text-[10px] font-semibold uppercase text-slate-500">Pareto · {stratum.dimension}</p>
          {series.bars.slice(0, 5).map((bar) => (
            <div key={bar.id} className="mt-0.5 grid grid-cols-[5.5rem_minmax(0,1fr)_4.5rem] items-center gap-1 text-[10px]">
              <span className="truncate">{bar.label}</span>
              <span className="h-2 bg-slate-100">
                <span className="block h-2 bg-[var(--brand-primary)]" style={{ width: `${Math.min(100, bar.percent)}%` }} />
              </span>
              <span className="text-right tabular-nums">{formatMeasureValue(bar.value)} · {formatMeasureValue(bar.percent)}%</span>
            </div>
          ))}
        </div>
      ) : null)}
      {strata.filter((stratum) => !charts.some((chart) => chart.stratum.id === stratum.id) && stratum.slices.length > 0).map((stratum) => (
        <p key={stratum.id} className="text-[11px]">
          {stratum.dimension}: {stratum.slices.map((slice) => {
            const count = slice.occurrenceCount == null ? '' : `${formatMeasureValue(slice.occurrenceCount)} occ.`;
            const amount = slice.amount == null ? '' : formatMeasureValue(slice.amount);
            return [slice.label, count, amount].filter(Boolean).join(' ');
          }).join(' · ')}
        </p>
      ))}
      {gemba.slice(0, 3).map((note) => (
        <p key={note.id} className="text-[11px]">{[note.fact, note.place, formatEventDate(note.observedAt)].filter(Boolean).join(' · ')}</p>
      ))}
      <EvidenceStrip records={evidence} />
    </Block>
  );
}

function EvidenceStrip({ records }: { records: IssueEvidenceRecord[] }) {
  const images = records.filter((record) => record.kind === 'image').slice(0, 3);
  const [urls, setUrls] = useState<Record<string, string>>({});
  const key = images.map((record) => record.id).join('|');
  useEffect(() => {
    let cancelled = false;
    if (images.length === 0) return undefined;
    void Promise.all(images.map(async (record) => {
      try {
        return [record.id, await evidenceSignedUrl(record.storage_path)] as const;
      } catch {
        return [record.id, ''] as const;
      }
    })).then((pairs) => {
      if (!cancelled) setUrls(Object.fromEntries(pairs.filter(([, url]) => url)));
    });
    return () => { cancelled = true; };
  }, [key]);
  if (records.length === 0) return null;
  return (
    <div className="mt-1 flex gap-1">
      {images.map((record) => urls[record.id] ? (
        <img key={record.id} src={urls[record.id]} alt={record.caption || record.name} className="h-10 w-14 border border-slate-300 object-cover" />
      ) : (
        <span key={record.id} className="text-[10px] text-slate-500">{record.caption || record.name}</span>
      ))}
      {records.filter((record) => record.kind !== 'image').slice(0, 2).map((record) => (
        <span key={record.id} className="text-[10px] text-slate-500">{record.caption || record.name}</span>
      ))}
    </div>
  );
}

function CauseAnalysis({ issue }: { issue: Issue }) {
  const branches = ensureIshikawa(issue.root_cause_analysis?.ishikawa);
  const top = branches.slice(0, 3);
  const bottom = branches.slice(3);
  const confirmed = isCauseConfirmed(issue);
  const cause = issue.root_cause_analysis?.root_cause_description?.trim() || issue.root_cause?.trim() || '';
  return (
    <Block number="3" title="Análise das causas">
      <div aria-label="Ishikawa">
        <div className="grid grid-cols-3 gap-1">{top.map((group) => <Bone key={group.category} category={group.category} causes={group.causes} />)}</div>
        <div className="my-1 h-1 bg-[var(--brand-primary)]" />
        <div className="grid grid-cols-3 gap-1">{bottom.map((group) => <Bone key={group.category} category={group.category} causes={group.causes} />)}</div>
      </div>
      <p className="mt-1 border border-[var(--brand-primary)] bg-[var(--brand-primary-soft)] px-1.5 py-1 text-[11px]">
        <span className="font-semibold">Causa raiz. </span>
        {confirmed && cause ? cause : '—'}
        {confirmed && issue.root_cause_analysis?.root_cause_category ? ` · ${ROOT_CAUSE_CATEGORY_LABELS[issue.root_cause_analysis.root_cause_category]}` : ''}
      </p>
    </Block>
  );
}

function Bone({ category, causes }: { category: RootCauseCategory; causes: Array<string | IshikawaHypothesis> }) {
  const items = causes.map((cause) => (
    typeof cause === 'string'
      ? { text: cause.trim(), probable: false, status: 'candidate' as const }
      : { text: hypothesisText(cause), probable: cause.probable, status: cause.status }
  )).filter((item) => item.text);
  return (
    <div className="min-h-[3.2rem] border border-slate-300 px-1 py-0.5">
      <p className="text-[9px] font-bold uppercase tracking-wide text-[var(--brand-primary)]">{ROOT_CAUSE_CATEGORY_LABELS[category]}</p>
      {items.length === 0 ? <p className="text-[10px] text-slate-400">—</p> : items.slice(0, 3).map((item) => (
        <p
          key={item.text}
          className={`truncate text-[10px] leading-tight ${item.status === 'confirmed' || item.probable ? 'font-semibold text-[#1e3a5f]' : ''} ${item.status === 'discarded' ? 'text-slate-400 line-through' : ''}`}
        >
          {item.text}
        </p>
      ))}
    </div>
  );
}

function ActionPlan({
  actions,
  countermeasures,
  assigneeNames,
}: {
  actions: EnterpriseAction[];
  countermeasures: ActionCountermeasureRecord[];
  assigneeNames: Record<string, string>;
}) {
  return (
    <Block number="4" title="Plano de ação">
      {actions.length === 0 ? <Empty /> : (
        <table className="w-full border-collapse text-[10px]">
          <thead>
            <tr className="bg-slate-100 text-left">
              <th className="border border-slate-300 px-1 py-0.5">Contramedida</th>
              <th className="border border-slate-300 px-1 py-0.5">Quem</th>
              <th className="border border-slate-300 px-1 py-0.5">Quando</th>
              <th className="border border-slate-300 px-1 py-0.5">Status</th>
            </tr>
          </thead>
          <tbody>
            {actions.map((action) => {
              const extension = countermeasures.find((item) => item.actionId === action.id);
              const detail = [
                extension?.whereText ? `Onde ${extension.whereText}` : '',
                extension?.howText ? `Como ${extension.howText}` : '',
                extension?.costAmount == null ? '' : `Quanto ${formatMeasureValue(extension.costAmount)} ${extension.costCurrency ?? ''}`.trim(),
              ].filter(Boolean).join(' · ');
              return (
                <tr key={action.id} className={action.status === 'cancelled' ? 'text-slate-400' : ''}>
                  <td className="border border-slate-300 px-1 py-0.5">
                    <p className={action.status === 'cancelled' ? 'line-through' : ''}>{action.title}</p>
                    {detail ? <p className="text-[9px] text-slate-500">{detail}</p> : null}
                  </td>
                  <td className="border border-slate-300 px-1 py-0.5">{action.assigned_to ? (assigneeNames[action.assigned_to] ?? '—') : '—'}</td>
                  <td className="border border-slate-300 px-1 py-0.5">{action.due_date ? formatEventDate(action.due_date) : '—'}</td>
                  <td className="border border-slate-300 px-1 py-0.5">{ACTION_STATUS_LABELS[action.status]}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </Block>
  );
}

function Execution({ actions, events }: { actions: EnterpriseAction[]; events: IssueMaspEventRecord[] }) {
  const progress = executionProgress(actions);
  const blocked = actions.filter((action) => action.status === 'blocked').length;
  const cancelled = actions.filter((action) => action.status === 'cancelled').length;
  const deviations = events.filter((event) => event.kind === 'deviation');
  return (
    <Block number="5" title="Execução">
      <p className="text-[11px]">{progress.completed} de {progress.active} concluídas · {blocked} bloqueadas · {cancelled} canceladas</p>
      {deviations.length === 0 ? <Empty /> : deviations.slice(0, 3).map((event) => (
        <p key={event.id} className="text-[11px]">Desvio · {formatEventDate(event.occurredOn || event.createdAt)} · {event.body || event.title || '—'}</p>
      ))}
    </Block>
  );
}

function Results({ issue, actions, measures }: { issue: Issue; actions: EnterpriseAction[]; measures: IssueMeasureRecord[] }) {
  const rows = measures.map(presentMeasureComparison);
  const chart = rows.filter((row) => row.before != null && row.after != null);
  const closure = evaluateIssueClosure({
    actions,
    attempts: efficacyAttempts(issue.metadata),
    closureReason: issue.metadata?.closure_reason,
    metadata: issue.metadata,
  });
  const latest = latestEfficacyAttempt(authorizingAttempts(efficacyAttempts(issue.metadata), latestReopenAt(issue.metadata)));
  const decision = closure.case === 'B' && closure.canClose ? 'Eficaz' : latest?.result === 'ineffective' ? 'Não eficaz' : '—';
  return (
    <Block number="6" title="Verificação dos resultados">
      {rows.length === 0 ? <Empty /> : (
        <table className="w-full border-collapse text-[10px]">
          <thead>
            <tr className="bg-slate-100 text-left">
              <th className="border border-slate-300 px-1 py-0.5">Indicador</th>
              <th className="border border-slate-300 px-1 py-0.5">Antes</th>
              <th className="border border-slate-300 px-1 py-0.5">Meta</th>
              <th className="border border-slate-300 px-1 py-0.5">Depois</th>
              <th className="border border-slate-300 px-1 py-0.5">Variação</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                <td className="border border-slate-300 px-1 py-0.5">{row.name}</td>
                <td className="border border-slate-300 px-1 py-0.5 tabular-nums">{formatMeasureValue(row.before)}</td>
                <td className="border border-slate-300 px-1 py-0.5 tabular-nums">{formatMeasureValue(row.target)}</td>
                <td className="border border-slate-300 px-1 py-0.5 tabular-nums">{formatMeasureValue(row.after)}</td>
                <td className="border border-slate-300 px-1 py-0.5">{variation(row.before, row.after)} · {row.result}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {chart.length > 0 ? (
        <div className="mt-1" aria-label="Antes e depois">
          {chart.map((row) => {
            const max = Math.max(Math.abs(row.before ?? 0), Math.abs(row.after ?? 0), 1);
            return (
              <div key={row.id} className="mt-0.5 grid grid-cols-[5rem_1fr] items-center gap-1 text-[10px]">
                <span className="truncate">{row.name}</span>
                <span className="flex h-3 gap-1">
                  <span className="h-3 bg-slate-400" style={{ width: `${(Math.abs(row.before ?? 0) / max) * 48}%` }} />
                  <span className="h-3 bg-[var(--brand-primary)]" style={{ width: `${(Math.abs(row.after ?? 0) / max) * 48}%` }} />
                </span>
              </div>
            );
          })}
        </div>
      ) : null}
      <p className="mt-1 text-[11px] font-semibold">Eficácia: {decision}{latest ? ` · ${new Date(latest.at).toLocaleDateString('pt-BR')}` : ''}</p>
    </Block>
  );
}

function variation(before: number | null, after: number | null): string {
  if (before == null || after == null) return '—';
  const delta = after - before;
  const sign = delta > 0 ? '+' : '';
  return `${sign}${formatMeasureValue(delta)}`;
}

function Standardization({ events }: { events: StandardizationEventRecord[] }) {
  const kinds: StandardizationKind[] = ['standard', 'training', 'communication', 'audit'];
  return (
    <Block number="7" title="Padronização">
      <table className="w-full border-collapse text-[10px]">
        <thead>
          <tr className="bg-slate-100 text-left">
            {kinds.map((kind) => <th key={kind} className="border border-slate-300 px-1 py-0.5">{STANDARDIZATION_KIND_LABEL[kind]}</th>)}
          </tr>
        </thead>
        <tbody>
          <tr>
            {kinds.map((kind) => {
              const found = events.filter((event) => event.kind === kind);
              const text = found.length === 0 ? '—' : found.slice(0, 2).map((event) => (
                event.kind === 'audit'
                  ? [event.result ? AUDIT_RESULT_LABEL[event.result] : '', event.title].filter(Boolean).join(' ')
                  : event.kind === 'communication'
                    ? (event.audience || event.body || '—')
                    : (event.title || event.body || '—')
              )).join(' · ');
              return <td key={kind} className="border border-slate-300 px-1 py-0.5 align-top">{text}</td>;
            })}
          </tr>
        </tbody>
      </table>
    </Block>
  );
}

function Conclusion({ issue }: { issue: Issue }) {
  const stage = workspaceStages(issue).find((item) => item.statuses.includes(issue.status));
  const status = stage ? presentStageLabel(issue.mode, stage.key, stage.label) : ISSUE_STATUS_LABELS[issue.status];
  const days = conclusionDurationDays(issue.created_at, issue.resolved_at);
  const reason = issue.metadata?.closure_reason?.trim() || '—';
  return (
    <Block number="8" title="Conclusão">
      <p className="text-[11px]">Situação: {issue.status === 'closed' ? 'Problema encerrado' : status}</p>
      <p className="text-[11px]">Motivo: {reason}</p>
      <p className="text-[11px]">
        Aberto {issue.created_at ? new Date(issue.created_at).toLocaleDateString('pt-BR') : '—'}
        {issue.resolved_at ? ` · Encerrado ${new Date(issue.resolved_at).toLocaleDateString('pt-BR')}` : ''}
        {days != null ? ` · ${days === 1 ? '1 dia' : `${days} dias`}` : ''}
      </p>
    </Block>
  );
}

function Empty() {
  return <p className="text-[11px] text-slate-400">—</p>;
}
